// Block 233 (group 1) — the one error surface a component can actually reach.
//
// There was a .toast-banner style, an App-level toast at App.js:464, and a global
// handler in index.js that appended a div itself — but showToast lived inside App and
// was never passed down or put in a context, so a child component's only option was
// alert(). Twelve components had rolled their own toast state.
//
// This is deliberately NOT a React context. It works from utils/ and services/ too,
// which no provider could reach, and those are where a third of the silent failures
// are. index.js's global handler and App.js's own toast both go through it, so every toast in
// the app has the one look (fix(toast), 2026-10-07).

import '../ui/toast.css';

// fix(toast), 2026-10-07 (the toast part of UI-3, done early). One dark ink bar, bottom centre:
// an icon tile (a tick for success, a cross for error, none for neutral), the message, and an
// optional action on the right, all on ONE row (the message wraps to two lines at most, never the
// action). The look lives in src/ui/toast.css. It was a coloured box set inline per type, and on
// a phone the Undo toast wrapped to three lines with Undo as a separate box beneath it, over the
// page and the Ask button.
const DEFAULT_MS = 6000;
export const TOAST_TYPES = ['success', 'error', 'info'];
const ICON = { success: '✓', error: '✕' };

// action: an optional { label, onClick } text button (FU-1 decision 45's "Undo"). Clicking it runs
// onClick and closes the toast. While it is pressed the toast stays open, and the remaining time
// resumes when it is let go. role="status" so screen readers announce it.
export function showToast(message, { type = 'error', ms = DEFAULT_MS, action = null } = {}) {
  if (typeof document === 'undefined' || !message) return;
  const kind = TOAST_TYPES.includes(type) ? type : 'error';
  const el = document.createElement('div');
  el.className = `toast-banner toast-banner--${kind}`;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  if (ICON[kind]) {
    const icon = document.createElement('span');
    icon.className = `toast-banner__icon toast-banner__icon--${kind}`;
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = ICON[kind];
    el.appendChild(icon);
  }
  const msg = document.createElement('span');
  msg.className = 'toast-banner__msg';
  msg.textContent = message;
  el.appendChild(msg);

  let timer = null;
  let deadline = Date.now() + ms;
  const close = () => { clearTimeout(timer); el.remove(); };
  const arm = (wait) => { clearTimeout(timer); deadline = Date.now() + wait; timer = setTimeout(close, wait); };

  if (action && action.label && typeof action.onClick === 'function') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'toast-banner__action';
    btn.textContent = action.label;
    btn.setAttribute('data-toast-action', '');
    let remaining = null;
    const hold = () => { if (remaining === null) { remaining = Math.max(1000, deadline - Date.now()); clearTimeout(timer); } };
    const release = () => { if (remaining !== null && el.isConnected) { const r = remaining; remaining = null; arm(r); } };
    btn.addEventListener('pointerdown', hold);
    btn.addEventListener('pointerup', release);
    btn.addEventListener('pointercancel', release);
    btn.addEventListener('pointerleave', release);
    btn.addEventListener('click', () => { close(); action.onClick(); });
    el.appendChild(btn);
  }

  document.body.appendChild(el);
  arm(ms);
  return el;
}

export const showErrorToast = (message, opts) => showToast(message, { ...opts, type: 'error' });
export const showSuccessToast = (message, opts) => showToast(message, { ...opts, type: 'success' });
